import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import marksatteled from "../../../../assests/marksatteled.svg";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import "./ScheduleReport.css";
import checkIconGreen from "../../../../assests/checkIconGreen.svg"
import compare from "../../../../assests/compare.svg"
import LoadingSpinner from "../../../Loading/component";

const TruncatedText = ({ text, maxLength = 20 }) => {
  const [showFullText, setShowFullText] = useState(false);

  if (!text || text.length <= maxLength) {
    return <span className="sms-truncated-text">{text || 'N/A'}</span>;
  }

  if (showFullText) {
    return (
      <span className="sms-truncated-text-full">
        {text}
        <button className="sms-see-less-btn" onClick={() => setShowFullText(false)}>
          See Less
        </button>
      </span>
    );
  }

  return (
    <span className="sms-truncated-text">
      {text.substring(0, maxLength)}...
      <button className="sms-see-more-btn" onClick={() => setShowFullText(true)}>
        See More
      </button>
    </span>
  );
};

const ScheduleMarkSettleDownloadReportComponent = ({
  loading,
  error,
  reportData,
  formatCurrency,
  onDownloadExcel,
  onGoBack,
  onMarkSettle,
  showMarkSettleDialog,
  setShowMarkSettleDialog,
  showSuccessDialog,
  onSuccessClose,
  onFullMatchUpload,
  showMismatchDialog,
  mismatchData,
  onMismatchCancel,
  onMismatchUpload,
  onFileSelect,
  checkError,
  isCheckingFile,
  uploadError,
  setUploadError,
  onRetryUpload,
  uploadedFileName,
  uploadedFileSize,
  mismatchType,
  mismatchDetails = [],
  showSimpleSuccessPopup,
  onSimpleSuccessClose,
  onResetFileSelection,
  fileHeaders = [],
  fileData = [],
  isUploading = false
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileSelected, setFileSelected] = useState(false);
  const [showInstructionText, setShowInstructionText] = useState(true);
  const [localError, setLocalError] = useState(null);
  const [isInvalidFile, setIsInvalidFile] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    console.log("🔍 Dialog Props:", {
      showSuccessDialog,
      showMismatchDialog,
      mismatchType,
      mismatchData,
      mismatchDetails: mismatchDetails?.length || 0,
      uploadedFileName,
      isUploading
    });
  }, [showSuccessDialog, showMismatchDialog, mismatchType, mismatchData, mismatchDetails, uploadedFileName, isUploading]);

  const resetFileSelection = () => {
    setSelectedFile(null);
    setFileSelected(false);
    setShowInstructionText(true);
    setLocalError(null);
    setIsInvalidFile(false);
    setShowFilePreview(false);
    
    if (setUploadError) {
      setUploadError(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidExtension = fileExtension === 'xls' || fileExtension === 'xlsx';
    
    if (validTypes.includes(file.type) && isValidExtension) {
      setSelectedFile(file);
      setFileSelected(true);
      setShowInstructionText(false);
      setLocalError(null);
      setIsInvalidFile(false);
      setShowFilePreview(true);
      
      if (setUploadError) {
        setUploadError(null);
      }
      
      if (onFileSelect) {
        onFileSelect(file);
      }
    } else {
      const errorMsg = 'Invalid file format. Please upload XLS file only.';
      setLocalError(errorMsg);
      setIsInvalidFile(true);
      setSelectedFile(null);
      setFileSelected(false);
      setShowInstructionText(true);
      setShowFilePreview(false);
      
      if (setUploadError) {
        setUploadError(errorMsg);
      }
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidExtension = fileExtension === 'xls' || fileExtension === 'xlsx';
    
    if (validTypes.includes(file.type) && isValidExtension) {
      setSelectedFile(file);
      setFileSelected(true);
      setShowInstructionText(false);
      setLocalError(null);
      setIsInvalidFile(false);
      setShowFilePreview(true);
      
      if (setUploadError) {
        setUploadError(null);
      }
      
      if (onFileSelect) {
        onFileSelect(file);
      }
    } else {
      const errorMsg = 'Invalid file format. Please upload XLS file only.';
      setLocalError(errorMsg);
      setIsInvalidFile(true);
      setSelectedFile(null);
      setFileSelected(false);
      setShowInstructionText(true);
      setShowFilePreview(false);
      
      if (setUploadError) {
        setUploadError(errorMsg);
      }
    }
  };

  const handleDialogClose = () => {
    setShowMarkSettleDialog(false);
    resetFileSelection();
    
    if (onResetFileSelection) {
      onResetFileSelection();
    }
  };

  const handleRemoveFileAndReset = () => {
    resetFileSelection();
  };

  const handleResetAndRetry = () => {
    resetFileSelection();
    
    if (onRetryUpload) {
      onRetryUpload();
    }
  };

  const handleRemoveFileFromPartialMatch = () => {
    resetFileSelection();
    onMismatchCancel();
    setTimeout(() => {
      onMarkSettle();
    }, 100);
  };

  const handleMarkSettleClick = () => {
    onMarkSettle();
  };

  const getFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) {
      return `${mb.toFixed(1)}MB`;
    }
    return `${kb.toFixed(1)}KB`;
  };

  const getPartialMatchMessage = () => {
    if (mismatchData) {
      const matchedCount = mismatchData.matchedCount || 0;
      const totalCount = mismatchData.totalCount || 0;
      const mismatchCount = mismatchData.mismatchCount || 0;
      return (
        <>
          Only <span className="sms-highlight-green">{matchedCount}</span> of <span className="sms-highlight-dark">{totalCount}</span> partners matched.
          {mismatchCount > 0 && ` ${mismatchCount} partner(s) have mismatch.`}
          <br />
          You can upload remaining Partner's UTR later.
        </>
      );
    }
    return "Some partners have payout mismatch. You can upload their UTR later.";
  };

  const getSuccessMessage = () => {
    if (mismatchData) {
      const matchedCount = mismatchData.matchedCount || 0;
      const totalCount = mismatchData.totalCount || 0;
      return (
        <>
          Great! Out of <span className="sms-highlight-green">{totalCount}</span>, <span className="sms-highlight-green">{matchedCount}</span> Partner has been matched.
        </>
      );
    }
    return "All partners matched successfully!";
  };

  const getErrorMessage = () => {
    if (localError) return localError;
    if (uploadError) return uploadError;
    if (checkError) return checkError;
    return null;
  };

  const displayError = getErrorMessage();
  const currentFileName = (!isInvalidFile && selectedFile?.name) || uploadedFileName;
  const currentFileSize = (!isInvalidFile && selectedFile?.size) ? getFileSize(selectedFile.size) : uploadedFileSize;

  return (
    <div className="sms-report-page-container">
      {loading ? (
        <div className="sms-mark-loading-spinner"> <LoadingSpinner /> </div>
      ) : error ? (
        <div className="sms-error-message">{error}</div>
      ) : (
        <>
          <Sidebar />
          <div className="sms-report-content-wrapper">
            <div className="sms-report-header">
              <button className="sms-report-back-btn" onClick={onGoBack}>
                ←
              </button>
              <h1 className="sms-report-date">
                {reportData.date
                  ? new Date(reportData.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                  : "N/A"}
              </h1>
            </div>
            <div className="sms-report-container">
              <div className="sms-table-container">
                <div className="sms-report-table-wrapper">
                  <table className="sms-report-table">
                    <thead>
                      <tr>
                        <th className="sms-table-header">Partner ID</th>
                        <th className="sms-table-header">Partner Name</th>
                        <th className="sms-table-header">Cohort ID</th>
                        <th className="sms-table-header">Status</th>
                        <th className="sms-table-header">Deductions</th>
                        <th className="sms-table-header">Payout</th>
                        <th className="sms-table-header">Transfer Test</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData && reportData.cohortScheduleResponseDto?.map((row) => (
                        <tr key={row.payoutId || row.payoutId} className="sms-table-row">
                          <td className="sms-table-cell">{row.franchiseeId || 'N/A'}</td>
                          <td className="sms-table-cell">{row.payee}</td>
                          <td className="sms-table-cell">{row.cohortId || 'N/A'}</td>
                          <td className="sms-table-cell">
                            <span className={`sms-generatedstatus-${(row.status || 'generated').toLowerCase()}`}>
                              {row.status || 'Generated'}
                            </span>
                          </td>
                          <td className="sms-table-cell">
                            <TruncatedText text={row.deductions} maxLength={20} />
                          </td>
                          <td className="sms-table-cell">{formatCurrency(row.payout || 0)}</td>
                          <td className="sms-table-cell">
                            <TruncatedText text={row.transferText} maxLength={20} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="sms-report-footer">
                <Button
                  onClick={onDownloadExcel}
                  variant="outlined"
                  fullWidth
                  className="sms-mark-download-btn"
                >
                  Download
                </Button>
                <Button
                  onClick={handleMarkSettleClick}
                  variant="contained"
                  className="sms-settle-btn"
                  fullWidth
                >
                  Mark Settle
                </Button>
              </div>
            </div>
          </div>

          {/* Upload UTR Dialog */}
          <Dialog
            open={showMarkSettleDialog}
            maxWidth="md"
            fullWidth={true}
            className="sms-upload-dialog"
            sx={{
              "& .MuiDialog-paper": {
                width: "100%",
                maxWidth: "878px",
                minHeight: "617px",
                borderRadius: "16px",
                position: "relative",
              },
            }}
          >
            <DialogTitle className="sms-upload-dialog-title">
              <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                className="sms-upload-dialog-close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent className="sms-upload-dialog-content">
              <Typography className="sms-upload-dialog-title-text">Upload UTR</Typography>
              
              {(!fileSelected || displayError || isInvalidFile) ? (
                <Box 
                  className={`sms-file-upload-area ${isDragOver ? 'sms-drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    id="sms-file-upload-input"
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                    className="sms-file-input-hidden"
                  />
                  <CloudUploadIcon className="sms-cloud-upload-icon" />
                  <Typography className="sms-upload-title">
                    Choose file or drag here
                  </Typography>
                  <Typography className="sms-upload-subtitle">
                    Supported format: XLS only
                  </Typography>
                </Box>
              ) : (
                <Box className="sms-file-info-box">
                  <Box className="sms-file-info-content">
                    <Typography className="sms-file-name">{currentFileName}</Typography>
                    <Typography className="sms-file-size">{currentFileSize}</Typography>
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={handleRemoveFileAndReset}
                    className="sms-file-remove-btn"
                    title="Remove file and upload new"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {isCheckingFile && (
                <div className="sms-checking-spinner-box">
                  <div className="sms-spinner-small"></div>
                  <span>Checking file...</span>
                </div>
              )}

              {displayError && (
                <Alert 
                  severity="error" 
                  className="sms-upload-error-alert" 
                  sx={{ mt: 2 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={handleResetAndRetry}
                      sx={{ fontWeight: 600 }}
                    >
                      Try Again
                    </Button>
                  }
                >
                  {displayError}
                </Alert>
              )}

              {showInstructionText && !fileSelected && !displayError && !isCheckingFile && !isInvalidFile && (
                <Typography className="sms-upload-instruction-note">
                  Please upload the corresponding UTR file for the selected payout.
                </Typography>
              )}
            </DialogContent>
          </Dialog>

          {/* ✅ FULL MATCH - Success Dialog with Upload button */}
          {showSuccessDialog && mismatchType === 'full_match' && (
            <Dialog
              open={true}
              onClose={onSuccessClose}
              fullWidth
              maxWidth="sm"
              className="sms-success-dialog"
              sx={{
                "& .MuiDialog-paper": {
                  width: "100%",
                  maxWidth: "878px",
                  minHeight: "617px",
                  borderRadius: "16px",
                  position: "relative",
                },
              }}
            >
              <DialogContent className="sms-success-dialog-content">
                <img className="sms-success-icon" src={marksatteled} alt="success" />
                <DialogTitle className="sms-success-title">Matching Completed</DialogTitle>
                <Typography className="sms-success-message">
                  {getSuccessMessage()}
                </Typography>
                
                {currentFileName && (
                  <Box className="sms-success-file-info">
                    <Typography className="sms-success-file-name">{currentFileName}</Typography>
                    <Typography className="sms-success-file-size">{currentFileSize}</Typography>
                  </Box>
                )}
                
                <Box className="sms-success-actions" sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <Button 
                    onClick={onSuccessClose} 
                    variant="outlined"
                    className="sms-success-cancel-btn"
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={onFullMatchUpload} 
                    className="sms-success-upload-btn" 
                    variant="contained"
                    disabled={isUploading || !currentFileName}
                    sx={{ minWidth: '100px' }}
                  >
                    {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          )}

          {/* ✅ PARTIAL MATCH - Dialog with Cancel & Upload buttons */}
          {showMismatchDialog && mismatchType === 'partial' && (
            <Dialog
              open={true}
              onClose={onMismatchCancel}
              fullWidth
              maxWidth="sm"
              className="sms-mismatch-dialog"
              sx={{
                "& .MuiDialog-paper": {
                  width: "100%",
                  maxWidth: "598px",
                  maxHeight: "80vh",
                  borderRadius: "20px",
                  position: "relative",
                  overflow: "hidden",
                },
              }}
            >
              <DialogTitle className="sms-mismatch-dialog-title-with-close">
                <IconButton
                  aria-label="close"
                  onClick={handleRemoveFileFromPartialMatch}
                  className="sms-mismatch-dialog-close"
                  title="Remove file and upload new"
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent className="sms-mismatch-dialog-content" sx={{ overflowY: 'auto' }}>
                <Box className="sms-mismatch-section">
                  <div className="sms-mismatch-header">
                    <img src={compare} alt="warning" style={{ width: 50, height: 50 }} />
                    <Typography className="sms-mismatch-title">
                      Partial Match Found
                    </Typography>
                  </div>
                  <Typography className="sms-mismatch-message">
                    {getPartialMatchMessage()}
                  </Typography>
                  
                  {currentFileName && (
                    <Box className="sms-mismatch-file-info">
                      <div className="sms-mismatch-file-details">
                        <span className="sms-mismatch-file-name">{currentFileName}</span>
                        <span className="sms-mismatch-file-size">{currentFileSize}</span>
                        <IconButton 
                          size="small" 
                          onClick={handleRemoveFileFromPartialMatch}
                          className="sms-mismatch-file-remove"
                          title="Remove file and upload new"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </Box>
                  )}

                  {/* Mismatch Details */}
                  {mismatchDetails && mismatchDetails.length > 0 && (
                    <Box className="sms-mismatch-details-box" sx={{ mt: 2 }}>
                      <Typography className="sms-mismatch-details-title">
                        Mismatch Details:
                      </Typography>
                      <Box className="sms-mismatch-details-list" sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {mismatchDetails.map((detail, index) => (
                          <Typography key={index} className="sms-mismatch-detail-item">
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions className="sms-mismatch-dialog-actions">
                <Button 
                  onClick={onMismatchCancel} 
                  variant="outlined" 
                  className="sms-mismatch-cancel-btn"
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={onMismatchUpload} 
                  variant="contained" 
                  className="sms-mismatch-upload-btn"
                  disabled={!currentFileName || isUploading}
                  sx={{ minWidth: '100px' }}
                >
                  {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {/* ✅ Simple Success Popup after upload */}
          <Dialog
            open={showSimpleSuccessPopup}
            onClose={onSimpleSuccessClose}
            fullWidth
            maxWidth="sm"
            className="sms-success-dialog"
          >
            <DialogContent className="sms-success-dialog-content">
              <img className="sms-success-icon" src={checkIconGreen} alt="success" />
              <DialogTitle className="sms-success-title">Successful</DialogTitle>
              <Typography className="sms-success-message">
                File Uploaded Successfully
              </Typography>
              <Button onClick={onSimpleSuccessClose} className="sms-success-ok-btn" variant="contained">
                OK
              </Button>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default ScheduleMarkSettleDownloadReportComponent;


// import React, { useState, useEffect } from "react";
// import {
//   Button,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogTitle,
//   TextField,
//   Typography,
//   IconButton,
//   Checkbox,
//   FormControlLabel,
//   Box
// } from '@mui/material';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import Sidebar from "../../../Navigation/Sidebar/Sidebar";
// import marksatteled from "../../../../assests/marksatteled.svg"
// import CloseIcon from "@mui/icons-material/Close";
// import "./ScheduleReport.css";
// import { connect } from "react-redux";
// import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
// import { useNavigate, useParams } from "react-router-dom";
// import { toastr } from "react-redux-toastr";
// import * as XLSX from 'xlsx';
// import debounce from 'lodash/debounce';
// import { format } from 'date-fns';
// import LoadingSpinner from "../../../Loading/component";
// const TruncatedText = ({ text, maxLength = 20 }) => {
//   const [showFullText, setShowFullText] = useState(false);

//   if (!text || text.length <= maxLength) {
//     return <span>{text || 'N/A'}</span>;
//   }

//   if (showFullText) {
//     return (
//       <span>
//         {text}
//         <button
//           className="see-less-btn"
//           onClick={() => setShowFullText(false)}
//         >
//           See Less
//         </button>
//       </span>
//     );
//   }

//   return (
//     <span>
//       {text.substring(0, maxLength)}...
//       <button
//         className="see-more-btn"
//         onClick={() => setShowFullText(true)}
//       >
//         See More
//       </button>
//     </span>
//   );
// };


// const ScheduleMarkSettleDownloadReportComponent = ({
//   loading,
//   error,
//   reportData,
//   formatCurrency,
//   onDownloadExcel,
//   onGoBack,
//   onMarkSettle,
//   showMarkSettleDialog,
//   setShowMarkSettleDialog,
//   selectedDate,
//   setSelectedDate,
//   onMarkSettledConfirm,
//   showSuccessDialog,
//   onSuccessClose,
//   selectedPayoutIds,
//   setSelectedPayoutIds,
//   selectAll,
//   setSelectAll,
//   payoutData
// }) => {
//   const [localSelectAll, setLocalSelectAll] = useState(false);

//   // Handle select all checkbox
//   const handleSelectAll = (event) => {
//     const isChecked = event.target.checked;
//     setLocalSelectAll(isChecked);

//     if (isChecked) {
//       // Select all payout IDs
//       const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
//       setSelectedPayoutIds(allPayoutIds);
//     } else {
//       // Clear selection
//       setSelectedPayoutIds([]);
//     }
//   };

//   // Handle individual checkbox selection
//   const handleCheckboxChange = (payoutId) => {
//     if (selectedPayoutIds.includes(payoutId)) {
//       // Remove from selection
//       setSelectedPayoutIds(selectedPayoutIds.filter(id => id !== payoutId));
//       setLocalSelectAll(false);
//     } else {
//       // Add to selection
//       setSelectedPayoutIds([...selectedPayoutIds, payoutId]);

//       // If all items are selected, update the select all checkbox
//       const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
//       if (selectedPayoutIds.length + 1 === allPayoutIds.length) {
//         setLocalSelectAll(true);
//       }
//     }
//   };

//   // Update local select all when selectedPayoutIds changes
//   useEffect(() => {
//     const allPayoutIds = reportData.cohortScheduleResponseDto?.map(item => item.payoutId) || [];
//     setLocalSelectAll(selectedPayoutIds.length === allPayoutIds.length && allPayoutIds.length > 0);
//   }, [selectedPayoutIds, reportData]);

//   return (
//     <div className="report-page-container">
//       {loading ? (
//         <div className="mark-loading-spinner"> <LoadingSpinner /> </div>
//       ) : error ? (
//         <div className="error-message">{error}</div>
//       ) : (
//         <>
//           <Sidebar />
//           <div className="report-content-wrapper">
//             <div className="report-header">
//               <button className="report-back-btn" onClick={onGoBack}>
//                 ←
//               </button>
//               <h1 className="report-date">
//                 {reportData.date
//                   ? new Date(reportData.date).toLocaleDateString("en-US", {
//                     year: "numeric",
//                     month: "long",
//                     day: "numeric",
//                   })
//                   : "N/A"}
//               </h1>
//             </div>
//             <div className="report-container">
//               <div className="table-container">
//                 <div className="report-table-wrapper">
//                   <table className="report-table">
//                     <thead>
//                       <tr>
//                         <th>
//                           <FormControlLabel
//                             control={
//                               <Checkbox
//                                 checked={localSelectAll}
//                                 onChange={handleSelectAll}
//                                 disabled={!reportData.cohortScheduleResponseDto || reportData.cohortScheduleResponseDto.length === 0}
//                               />
//                             }
//                             label="Select All"
//                           />
//                         </th>
//                         <th>Partner ID</th>
//                         <th>Partner Name</th>
//                         <th>Cohort ID</th>
//                         <th>Status</th>
//                         <th>Deductions</th>
//                         <th>Payout</th>
//                         <th>Transfer Test</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {reportData && reportData.cohortScheduleResponseDto?.map((row) => (
//                         <tr key={row.payoutId || row.payoutId}>
//                           <td>
//                             <Checkbox
//                               checked={selectedPayoutIds.includes(row.payoutId)}
//                               onChange={() => handleCheckboxChange(row.payoutId)}
//                             />
//                           </td>
//                           <td>{row.franchiseeId || 'N/A'}</td>

//                           <td>

//                             {row.payee}
//                           </td>
//                            <td>

//                             {row.cohortId || 'N/A'}
//                           </td>
//                           <td>
//                             <span className={`generatedstatus-${(row.status || 'generated').toLowerCase()}`}>
//                               {row.status || 'Generated'}
//                             </span>
//                           </td>
//                           <td>
//                             <TruncatedText text={row.deductions} maxLength={20} />

//                           </td>
//                           <td>{formatCurrency(row.payout || 0)}</td>
//                           <td>
//                             <TruncatedText text={row.transferText} maxLength={20} />
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 </div>
//               </div>

//               {/* Footer buttons */}
//               <div className="report-footer">
//                 <Button
//                   onClick={onDownloadExcel}
//                   variant="outlined"
//                   fullWidth
//                   className="mark-download-btn"
//                 >
//                   Download
//                 </Button>
//                 <Button
//                   onClick={() => onMarkSettle(selectedPayoutIds)}
//                   variant="contained"
//                   color="secondary"
//                   className="settle-btn"
//                   fullWidth
//                   disabled={selectedPayoutIds.length === 0}
//                 >
//                   Mark Settle ({selectedPayoutIds.length})
//                 </Button>
//               </div>
//             </div>
//           </div>

//           {/* Mark Settle Dialog */}
//           <LocalizationProvider dateAdapter={AdapterDateFns}>
//             <Dialog
//               open={showMarkSettleDialog}
//               maxWidth="sm"
//               fullWidth={true}
//               sx={{
//                 "& .MuiDialog-paper": {
//                   width: "100%",
//                   maxWidth: "500px",
//                   minHeight: "280px",
//                   position: "relative",
//                   borderRadius: "8px",
//                 },
//               }}
//             >
//               <DialogTitle
//                 sx={{
//                   textAlign: "center",
//                   fontWeight: "bold",
//                   fontSize: "1.25rem",
//                   padding: "20px 24px 16px",
//                   position: "relative",

//                 }}
//               >
//                 Mark Settle
//                 <IconButton
//                   aria-label="close"
//                   onClick={() => setShowMarkSettleDialog(false)}
//                   sx={{
//                     position: "absolute",
//                     right: 16,
//                     top: 16,
//                     color: "#666"
//                   }}
//                 >
//                   <CloseIcon />
//                 </IconButton>
//               </DialogTitle>

//               <DialogContent sx={{ padding: "24px" }}>
//                 <Box sx={{ marginBottom: "20px" }}>
//                   <DatePicker
//                     label="Select Date to Mark this Payout as Settled"
//                     value={selectedDate}
//                     onChange={(newValue) => setSelectedDate(newValue)}
//                     renderInput={(params) => (
//                       <TextField
//                         {...params}
//                         fullWidth
//                         margin="normal"
//                         sx={{
//                           "& .MuiInputLabel-root": {
//                             fontSize: "14px"
//                           },
//                           "& .MuiInputBase-root": {
//                             fontSize: "14px"
//                           }
//                         }}
//                       />
//                     )}
//                   />
//                 </Box>
//                 <Typography variant="body2" sx={{
//                   mt: 2,
//                   textAlign: "center",
//                   fontSize: "14px",
//                   color: "#666"
//                 }}>
//                   Selected Payouts: {selectedPayoutIds.length}
//                 </Typography>
//               </DialogContent>

//               <DialogActions sx={{
//                 justifyContent: "center",
//                 padding: "16px 24px 24px",

//               }}>
//                 <Button
//                   onClick={onMarkSettledConfirm}
//                   color="success"
//                   variant="contained"
//                   disabled={!selectedDate || selectedPayoutIds.length === 0}
//                   sx={{
//                     width: "150px",
//                     padding: "8px 24px",
//                     fontSize: "14px",
//                     fontWeight: "600"
//                   }}
//                 >
//                   DONE
//                 </Button>
//               </DialogActions>
//             </Dialog>

//             {/* Success Dialog */}
//             <Dialog
//               open={showSuccessDialog}
//               onClose={onSuccessClose}
//               fullWidth
//               maxWidth="xs"
//               sx={{
//                 "& .MuiDialog-paper": {
//                   borderRadius: "8px",
//                   padding: "0",
//                 }
//               }}
//             >
//               <DialogContent
//                 sx={{
//                   display: "flex",
//                   flexDirection: "column",
//                   alignItems: "center",
//                   textAlign: "center",
//                   padding: "32px 24px 24px",
//                 }}
//               >
//                 <img
//                   style={{
//                     height: "70px",
//                     width: "70px",
//                     marginBottom: "20px"
//                   }}
//                   src={marksatteled}
//                   alt="success"
//                 />
//                 <DialogTitle sx={{
//                   textAlign: "center",
//                   padding: "0",
//                   marginBottom: "8px",
//                   fontSize: "1.25rem",
//                   fontWeight: "bold"
//                 }}>
//                   Marked Settled
//                 </DialogTitle>
//                 <Typography sx={{
//                   marginBottom: "24px",
//                   fontSize: "14px",
//                   color: "#666"
//                 }}>
//                   {selectedPayoutIds.length} payout(s) have been marked as settled.
//                 </Typography>
//                 <Button
//                   onClick={onSuccessClose}
//                   sx={{
//                     backgroundColor: "#14AE78",
//                     color: "white",
//                     padding: "8px 24px",
//                     fontSize: "14px",
//                     fontWeight: "600",
//                     width: "150px",
//                     "&:hover": {
//                       backgroundColor: "#119966"
//                     }
//                   }}
//                   variant="contained"
//                 >
//                   OK
//                 </Button>
//               </DialogContent>
//             </Dialog>
//           </LocalizationProvider>
//         </>
//       )}
//     </div>
//   );
// };

// export default ScheduleMarkSettleDownloadReportComponent;